import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import useScrollAnimation from '../hooks/useScrollAnimation';

const honors = [
  { year: "2025", desc: "第二届「长城杯」铁人三项总决赛 网络渗透专项第一", level: "gold" },
  { year: "2025", desc: "第十八届全国大学生信息安全竞赛实践赛 全国二等奖", level: "silver" },
  { year: "2024", desc: "数字中国创新大赛数据安全产业人才积分争夺赛 金奖冠军", level: "gold" },
  { year: "2024", desc: "全国大学生信息安全竞赛 半决赛一等奖", level: "gold" },
  { year: "2024", desc: "全国高校网络安全管理运维赛 赛区第一名", level: "gold" },
  { year: "2024", desc: "铸剑杯全国大学生网络安全攻防竞赛一等奖", level: "gold" },
  { year: "", desc: "阿里大学生网络安全技能大赛亚军", level: "silver" },
  { year: "", desc: "百度大学生网络安全技能大赛团队第三名", level: "bronze" },
  { year: "", desc: "美团网络安全高校挑战赛冠军", level: "gold" },
  { year: "2023", desc: "首届盘古杯电子数据取证大赛全国二等奖", level: "silver" },
];

const levelColors = {
  gold: { color: '#DAA520', label: '金奖' },
  silver: { color: '#8B8B8B', label: '银奖' },
  bronze: { color: '#8B4513', label: '铜奖' }
};

const availableYears = [...new Set(honors.map(h => h.year).filter(y => y))].sort((a, b) => b - a);

function HonorsSection() {
  const [input, setInput] = useState('');
  const [history, setHistory] = useState([]);
  const [sectionRef, isSectionVisible] = useScrollAnimation(0.1);
  const terminalRef = useRef(null);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  const handleInput = (e) => {
    if (e.key === 'Enter' && input.trim()) {
      const command = input.trim();
      const newHistory = [...history, { type: 'input', text: `$ ${command}` }];

      if (command === 'clear') {
        setHistory([]);
        setInput('');
        return;
      }

      if (command === 'all') {
        newHistory.push({ type: 'output', text: '=== 所有荣誉 ===' });
        const grouped = {};
        honors.forEach(honor => {
          const year = honor.year || '其他';
          if (!grouped[year]) grouped[year] = [];
          grouped[year].push(honor);
        });

        Object.keys(grouped).sort((a, b) => b - a).forEach(year => {
          newHistory.push({ type: 'year', text: year });
          grouped[year].forEach(honor => {
            newHistory.push({ 
              type: 'honor', 
              text: honor.desc,
              level: honor.level
            });
          });
        });
      } else if (availableYears.includes(command)) {
        const yearHonors = honors.filter(h => h.year === command);
        newHistory.push({ type: 'output', text: `=== ${command}年荣誉 ===` });
        yearHonors.forEach(honor => {
          newHistory.push({ 
            type: 'honor', 
            text: honor.desc,
            level: honor.level
          });
        });
        newHistory.push({ type: 'output', text: `共找到 ${yearHonors.length} 项荣誉` });
      } else {
        newHistory.push({ type: 'error', text: `错误：未找到 "${command}" 的荣誉记录` });
        newHistory.push({ type: 'system', text: '可用年份：' + availableYears.join(', ') });
      }

      setHistory(newHistory);
      setInput('');
    }
  };

  return (
    <section ref={sectionRef} className="section honors" aria-labelledby="honors-title" style={{
      background: '#FFFFFF',
      overflow: 'hidden',
      opacity: isSectionVisible ? 1 : 0,
      transform: isSectionVisible ? 'translateY(0)' : 'translateY(50px)',
      transition: 'all 1s cubic-bezier(0.23, 1, 0.32, 1)',
      padding: '0',
      width: '60%',
      display: 'flex',
      flexDirection: 'column',
      margin: '0',
      paddingTop: '0',
      paddingBottom: '0'
    }}>
      <div className="container" style={{ maxWidth: '100%', margin: '0' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{
            fontFamily: "'Courier New', monospace",
            fontSize: '14px'
          }}
        >
          <div style={{
            padding: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#CD5C5C'
              }} />
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#DAA520'
              }} />
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#8B4513'
              }} />
            </div>
            <span style={{
              color: '#8B4513',
              fontSize: '12px'
            }}>
              yulinsec@honors:~
            </span>
          </div>

          <div 
            ref={terminalRef}
            style={{
              padding: '20px',
              height: '400px',
              overflowY: 'auto',
              color: '#2C2C2C',
              lineHeight: '1.6'
            }}
          >
            {history.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  marginBottom: '8px',
                  wordBreak: 'break-word'
                }}
              >
                {item.type === 'input' && (
                  <span style={{ color: '#2C2C2C' }}>{item.text}</span>
                )}
                {item.type === 'system' && (
                  <span style={{ color: '#8B4513' }}>{item.text}</span>
                )}
                {item.type === 'output' && (
                  <span style={{ color: '#CD5C5C', fontWeight: 'bold' }}>{item.text}</span>
                )}
                {item.type === 'error' && (
                  <span style={{ color: '#A05252' }}>{item.text}</span>
                )}
                {item.type === 'year' && (
                  <span style={{ 
                    color: '#8B4513', 
                    fontWeight: 'bold',
                    display: 'block',
                    marginTop: '15px',
                    marginBottom: '5px',
                    borderBottom: '1px solid #8B4513',
                    paddingBottom: '5px'
                  }}>
                    {item.text}
                  </span>
                )}
                {item.type === 'honor' && (
                  <div style={{
                    marginLeft: '20px',
                    color: '#4A4A4A',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '5px'
                  }}>
                    <span style={{ color: '#CD5C5C' }}>➜</span>
                    <span>{item.text}</span>
                    <span style={{
                      color: levelColors[item.level].color,
                      fontSize: '12px',
                      padding: '2px 8px',
                      borderRadius: '3px',
                      background: 'rgba(139, 69, 19, 0.1)'
                    }}>
                      {levelColors[item.level].label}
                    </span>
                  </div>
                )}
              </motion.div>
            ))}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginTop: '10px'
            }}>
              <span style={{ color: '#8B4513', marginRight: '8px' }}>$</span>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleInput}
                placeholder="输入年份..."
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  color: '#2C2C2C',
                  fontFamily: "'Courier New', monospace",
                  fontSize: '14px',
                  outline: 'none',
                  caretColor: '#8B4513'
                }}
                autoFocus
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default HonorsSection;